import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  PhoneOutgoing, PhoneCall, MessageSquare, StickyNote, Calendar, ChevronRight,
  Zap, Upload, ClipboardList, Radio, Flame, CheckCircle2, XCircle,
  Clock, TrendingUp, Users, AlertCircle, Play, RefreshCw, ArrowRight,
  ChevronDown, ChevronUp, ChevronsUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatPhone } from '../lib/utils';
import { useOutbound, useOutboundStats, useOutboundLoaded, OutboundStore } from '../lib/outboundStore';
import { useAuth } from '../lib/authStore';
import { useTeam, TeamStore } from '../lib/teamStore';
import LogCallModal from '../components/outbound/LogCallModal';
import FocusMode from '../components/outbound/FocusMode';

/* ────────────────────────────────────────────────────────────────────
   OUTBOUND WORKSPACE
   Deliberately styled as a "dialer / production dashboard" — dark hero
   with mono KPI cards up top, a horizontal filter rail, and a compact
   dialer-first table below. Same brand DNA (gold accents, monochrome,
   rounded cards) but visibly different from /leads so an LO knows
   they've left the primary CRM and entered prospecting mode.
   ──────────────────────────────────────────────────────────────── */

// Dialer-visible statuses. `not_interested` (and other dialer-exit
// dispositions) is intentionally NOT a tab — once an LO marks a lead
// as not interested or opt-out, the record stays in the DB for
// reporting but drops out of every dialer view. If an admin needs to
// audit opt-outs they go to Reports, not the dialer.
const OUTBOUND_STATUSES = [
  { key: 'all',            label: 'My Queue',        icon: ClipboardList },
  { key: 'new',            label: 'New',             icon: Radio },
  { key: 'attempted',      label: 'Attempted',       icon: PhoneCall },
  { key: 'follow_up',      label: 'Follow-Up',       icon: Clock },
  { key: 'appointment',    label: 'Appointment',     icon: Calendar },
  { key: 'hot',            label: 'Hot',             icon: Flame },
];

// Admin sees the firm-wide queue, so relabel the 'all' chip to reflect scope.
function scopedLabel(key, isAdmin) {
  if (key === 'all') return isAdmin ? 'All Outbound' : 'My Queue';
  return OUTBOUND_STATUSES.find((s) => s.key === key)?.label || key;
}

const STATUS_STYLE = {
  new:            { chip: 'bg-neutral-900 text-white',                       dot: 'bg-white' },
  attempted:      { chip: 'bg-amber-100 text-amber-900 border border-amber-200', dot: 'bg-amber-500' },
  contacted:      { chip: 'bg-sky-100 text-sky-900 border border-sky-200',   dot: 'bg-sky-500' },
  follow_up:      { chip: 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/40', dot: 'bg-[hsl(var(--kf-gold))]' },
  appointment:    { chip: 'bg-violet-100 text-violet-900 border border-violet-200', dot: 'bg-violet-500' },
  hot:            { chip: 'bg-red-600 text-white',                           dot: 'bg-white' },
  not_interested: { chip: 'bg-neutral-100 text-neutral-500 border border-neutral-200', dot: 'bg-neutral-400' },
};

const STATUS_LABEL = {
  new: 'New', attempted: 'Attempted', contacted: 'Contacted',
  follow_up: 'Follow-Up', appointment: 'Appointment', hot: 'Hot',
  not_interested: 'Not Interested',
};

const CALL_RESULT_LABEL = {
  connected: 'Connected', no_answer: 'No Answer', left_vm: 'Left VM',
  wrong_number: 'Wrong Number', not_interested: 'Not Interested',
  bad_number: 'Bad Number', do_not_call: 'Opt Out', callback: 'Requested Callback',
};

function formatRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const ms = Date.now() - t;
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatFuture(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Kinfolk operates from Arizona — pin follow-up times to America/Phoenix
  // so the dialer doesn't disagree with the LO's actual working hours if
  // their laptop timezone is off. Phoenix doesn't observe DST → always MST.
  const TZ = 'America/Phoenix';
  const now = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ }); // YYYY-MM-DD
  const dKey = dayFmt.format(d);
  const time = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
  if (dKey === dayFmt.format(now)) return `Today · ${time}`;
  if (dKey === dayFmt.format(tomorrow)) return `Tomorrow · ${time}`;
  return `${d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' })} · ${time}`;
}

// Created / imported-on date. Shorter than formatRelative because the
// column is narrow — shows "Today", "Yesterday", "3d ago", "Mar 12".
function formatCreated(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const TZ = 'America/Phoenix';
  // Compare calendar days in PHOENIX (not the viewer's tz) so a lead
  // imported at 11 PM Phoenix on Monday still reads "Today" for the
  // rest of Phoenix Monday, regardless of the LO's laptop tz.
  const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ });
  const now = new Date();
  const [ny, nm, nd] = dayFmt.format(now).split('-').map(Number);
  const [dy, dmm, dd] = dayFmt.format(d).split('-').map(Number);
  const nowDay = Date.UTC(ny, nm - 1, nd);
  const rowDay = Date.UTC(dy, dmm - 1, dd);
  const dayDiff = Math.round((nowDay - rowDay) / 86400000);
  if (dayDiff <= 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return `${dayDiff}d ago`;
  const yearFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric' });
  const sameYear = yearFmt.format(d) === yearFmt.format(now);
  return d.toLocaleDateString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: '2-digit' }),
  });
}
function formatFullDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Pinned to America/Phoenix (see formatFuture for the why).
  return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', dateStyle: 'medium', timeStyle: 'short' });
}

// State column pulls from lead.application.currentState / .state — same
// heuristic the /leads table uses so display is consistent.
function deriveState(l) {
  const app = l.application || {};
  return (app.currentState || app.propertyState || '').toUpperCase().slice(0, 2) || '—';
}

export default function Outbound() {
  const { user, isAdmin } = useAuth();
  const leads = useOutbound();
  const stats = useOutboundStats();
  const loaded = useOutboundLoaded();
  const teamMembers = useTeam(); // hydrate team so admin can see LO initials on rows

  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  // Admin-only LO scope: 'all' shows firm-wide, else a specific LO's queue.
  // LOs never see this control — their queue is server-scoped to `me` already.
  const [loFilter, setLoFilter] = useState('all');
  const [logCallLead, setLogCallLead] = useState(null);
  const [focusIndex, setFocusIndex] = useState(null);
  const [focusList, setFocusList] = useState(null); // frozen queue for the current Focus session
  const navigate = useNavigate();
  // Row-level selection so LOs can hand-pick who lands in the dialer.
  // Empty set + Start Calling → falls back to the filtered queue.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Column sort state — `col=null` preserves the store's natural order
  // (freshest queue first). Clicking a header cycles asc → desc → clear.
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const cycleSort = (col) => {
    setSort((prev) => {
      if (prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return { col: null, dir: 'asc' }; // third click clears
    });
  };

  // Admin LO scope applies BEFORE status filter so KPI + chip counts +
  // rows all reflect the selected officer's slice of the queue.
  const scoped = useMemo(() => {
    if (!isAdmin || loFilter === 'all') return leads;
    if (loFilter === 'unassigned') return leads.filter((l) => !l.assignedTo);
    return leads.filter((l) => l.assignedTo === loFilter);
  }, [leads, isAdmin, loFilter]);

  const filtered = useMemo(() => {
    const query = q.toLowerCase().trim();
    return scoped.filter((l) => {
      // "All Outbound" only shows leads still in the dialer queue.
      // Promoted-hot rows (outbound cleared to null/false, but
      // outboundStatus='hot') stay in the cache so the Hot chip can
      // surface them — they're already living in the main CRM and
      // must not appear under "All".
      if (filter === 'all' && l.outbound !== true) return false;
      if (filter !== 'all' && (l.outboundStatus || 'new') !== filter) return false;
      if (!query) return true;
      return (
        (l.name || '').toLowerCase().includes(query) ||
        (l.phone || '').includes(query) ||
        (l.email || '').toLowerCase().includes(query) ||
        (l.outboundCampaign || '').toLowerCase().includes(query)
      );
    });
  }, [scoped, filter, q]);

  // Sortable table — extractors return a comparable primitive (string
  // or number). Nulls / empties are always pushed to the bottom of an
  // ascending sort, so a click-to-sort never buries rows the LO wants
  // to see first.
  const SORT_EXTRACTORS = {
    name:        (l) => (l.name || '').toLowerCase(),
    phone:       (l) => (l.phone || '').replace(/\D/g, ''),
    state:       (l) => (l.state || l.propertyState || '').toLowerCase(),
    campaign:    (l) => (l.outboundCampaign || '').toLowerCase(),
    attempts:    (l) => Number(l.outboundAttempts || 0),
    last_call:   (l) => (l.outboundLastCallAt ? new Date(l.outboundLastCallAt).getTime() : 0),
    appointment: (l) => (l.outboundNextFollowUpAt ? new Date(l.outboundNextFollowUpAt).getTime() : 0),
    status:      (l) => l.outboundStatus || 'new',
    created:     (l) => {
      const raw = l.created_at || l.createdAt || l.outboundImportedAt;
      return raw ? new Date(raw).getTime() : 0;
    },
  };

  const sorted = useMemo(() => {
    if (!sort.col) return filtered;
    const extract = SORT_EXTRACTORS[sort.col];
    if (!extract) return filtered;
    // Copy before sorting — never mutate the store's cache.
    const rows = filtered.slice();
    rows.sort((a, b) => {
      const va = extract(a);
      const vb = extract(b);
      // Push falsy values (0, '', null) to the bottom regardless of dir
      // so an ascending sort on "Attempts" still shows worked leads
      // together, not empties on top.
      const aEmpty = va === 0 || va === '' || va == null;
      const bEmpty = vb === 0 || vb === '' || vb == null;
      if (aEmpty && !bEmpty) return 1;
      if (!aEmpty && bEmpty) return -1;
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort.col, sort.dir]);

  const counts = useMemo(() => {
    // "All Outbound" mirrors the KPI card exactly — count only leads
    // still living in the dialer queue (outbound === true). Promoted
    // hot leads have `outbound` cleared (null or false); they're
    // merged into the cache solely so the Hot chip can surface them,
    // and must NOT inflate the "All" total or the header KPI.
    const activeQueue = scoped.filter((l) => l.outbound === true);
    const c = { all: activeQueue.length };
    OUTBOUND_STATUSES.forEach((s) => { if (s.key !== 'all') c[s.key] = 0; });
    scoped.forEach((l) => {
      const k = l.outboundStatus || 'new';
      if (c[k] !== undefined) c[k] += 1;
    });
    return c;
  }, [scoped]);

  // When admin drills into one LO, recompute KPI tiles client-side off
  // the scoped cache so the header header + chips stay in lockstep.
  // Firm-wide (loFilter='all') falls back to the backend `/outbound/stats`
  // payload which is the source of truth for admin-wide totals.
  const displayStats = useMemo(() => {
    if (!isAdmin || loFilter === 'all') return stats;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const activeQueue = scoped.filter((l) => l.outbound === true);
    const has = (s) => activeQueue.filter((l) => (l.outboundStatus || 'new') === s).length;
    return {
      total: activeQueue.length,
      unworked: activeQueue.filter((l) => !l.outboundStatus || l.outboundStatus === 'new').length,
      contacted: activeQueue.filter((l) => ['contacted', 'follow_up', 'appointment'].includes(l.outboundStatus)).length,
      follow_ups: has('follow_up'),
      appointments: has('appointment'),
      calls_today: scoped.filter((l) => {
        if (!l.outboundLastCallAt) return false;
        const t = new Date(l.outboundLastCallAt);
        return !Number.isNaN(t.getTime()) && t >= todayStart;
      }).length,
      // Hot count includes promoted rows (outbound cleared) — mirrors the chip.
      hot_leads: scoped.filter((l) => l.outboundStatus === 'hot').length,
    };
  }, [isAdmin, loFilter, stats, scoped]);

  // Ordered queue that Focus Mode walks through — selected rows first
  // (LO's explicit picks), else the filtered set minus Not Interested
  // (archive rows shouldn't jump into the dialer).
  const focusQueue = useMemo(() => {
    if (selectedIds.size > 0) {
      // Preserve the on-screen order + filter but only include picked rows.
      return filtered.filter((l) => selectedIds.has(l.id));
    }
    return filtered.filter((l) => (l.outboundStatus || 'new') !== 'not_interested');
  }, [filtered, selectedIds]);

  // Prune stale ids that were selected but no longer visible (filter
  // change, promotion, etc.) so counts + Start Calling stay honest.
  useEffect(() => {
    if (selectedIds.size === 0) return;
    const visible = new Set(filtered.map((l) => l.id));
    let changed = false;
    const next = new Set();
    selectedIds.forEach((id) => {
      if (visible.has(id)) next.add(id);
      else changed = true;
    });
    if (changed) setSelectedIds(next);
  }, [filtered, selectedIds]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id));
  const someVisibleSelected = filtered.some((l) => selectedIds.has(l.id));

  // Anchor for Shift-click range select — the id of the last row the
  // LO toggled with a plain click. Reset on filter / sort / clear so
  // ranges never cross stale visual boundaries.
  const [rangeAnchor, setRangeAnchor] = useState(null);
  useEffect(() => { setRangeAnchor(null); }, [filter, loFilter, q, sort.col, sort.dir]);

  const toggleSelect = (leadId, event) => {
    // Shift-click range select — only works when there's an anchor from
    // a previous plain click AND the anchor is still in the sorted view.
    // Otherwise behaves like a plain toggle (and sets the new anchor).
    if (event?.shiftKey && rangeAnchor && rangeAnchor !== leadId) {
      const ids = sorted.map((l) => l.id);
      const anchorIdx = ids.indexOf(rangeAnchor);
      const clickIdx = ids.indexOf(leadId);
      if (anchorIdx !== -1 && clickIdx !== -1) {
        const [lo, hi] = anchorIdx <= clickIdx ? [anchorIdx, clickIdx] : [clickIdx, anchorIdx];
        // "Paint" mode mirrors Finder/Gmail: the range takes the same
        // state as the clicked row — if the clicked row ends up selected,
        // every row in the range is selected; if it ends up deselected,
        // every row in the range is deselected. We decide based on the
        // CURRENT state of the clicked row (which Shift-click inverts).
        const willSelect = !selectedIds.has(leadId);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (let i = lo; i <= hi; i += 1) {
            if (willSelect) next.add(ids[i]);
            else next.delete(ids[i]);
          }
          return next;
        });
        setRangeAnchor(leadId);
        return;
      }
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
    setRangeAnchor(leadId);
  };
  const selectAllVisible = () => {
    if (allVisibleSelected) {
      // Toggle off — deselect only the currently-visible rows.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filtered.forEach((l) => next.add(l.id));
        return next;
      });
    }
  };
  const clearSelection = () => setSelectedIds(new Set());

  const startCalling = () => {
    if (focusQueue.length === 0) {
      toast(selectedIds.size > 0 ? 'No selected leads match this filter.' : 'No leads in queue to work.');
      return;
    }
    // Freeze the queue at start-time — subsequent selection / filter
    // changes shouldn't shrink the session mid-dial.
    setFocusList(focusQueue);
    setFocusIndex(0);
  };

  return (
    <div data-testid="outbound-page" className="pb-12">
      {/* HERO — dark dialer strip. Feels like a call-center HUD. */}
      <div className="relative overflow-hidden rounded-2xl border border-neutral-900 bg-neutral-950 text-white mb-6">
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(1200px_400px_at_20%_-10%,hsl(var(--kf-gold))_0%,transparent_60%)]" />
        <div className="relative px-6 py-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.24em] text-white/50">
                <PhoneOutgoing className="h-3 w-3" /> Outbound · Prospecting Workspace
              </div>
              <h1 className="mt-1 text-3xl font-black tracking-tight">
                Dialer <span className="text-[hsl(var(--kf-gold))]">Queue</span>
              </h1>
              <p className="mt-1 text-[13px] text-white/60 max-w-xl">
                Work cold leads without polluting the main CRM. Set a lead to <b className="text-[hsl(var(--kf-gold))]">Hot</b> and it slides into Leads with every note and call already attached.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="outbound-start-calling"
                onClick={startCalling}
                className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--kf-gold))] px-4 py-2.5 text-[13px] font-bold text-neutral-950 shadow-[0_6px_20px_-6px_hsl(var(--kf-gold))] hover:brightness-110 transition"
              >
                <Play className="h-4 w-4" strokeWidth={3} />
                {selectedIds.size > 0 ? `Start Calling · ${selectedIds.size} selected` : 'Start Calling'}
                {selectedIds.size === 0 && (
                  <span className="ml-1 rounded-md bg-neutral-950/20 px-1.5 py-0.5 text-[10.5px] tabular-nums">
                    {focusQueue.length}
                  </span>
                )}
              </button>
              {isAdmin && (
                <button
                  data-testid="outbound-import-btn"
                  onClick={() => navigate('/outbound/import')}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-white/10 transition"
                >
                  <Upload className="h-4 w-4" /> Import CSV
                </button>
              )}
              <button
                onClick={() => { OutboundStore.refresh(); toast.success('Refreshed'); }}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-white/10 transition"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* KPI GRID — bright numbers on dark card faces. */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
            <KpiTile label="Outbound Leads"   value={displayStats.total}         icon={Users} />
            <KpiTile label="Unworked"         value={displayStats.unworked}      icon={Radio}         tone="warn" />
            <KpiTile label="Calls Today"      value={displayStats.calls_today}   icon={PhoneCall}     tone="gold" />
            <KpiTile label="Contacted"        value={displayStats.contacted}     icon={CheckCircle2}  tone="sky" />
            <KpiTile label="Follow-Ups"       value={displayStats.follow_ups}    icon={Clock}         tone="gold" />
            <KpiTile label="Appointments"     value={displayStats.appointments}  icon={Calendar}      tone="violet" />
            <KpiTile label="Hot Leads"        value={displayStats.hot_leads}     icon={Flame}         tone="hot" />
          </div>
        </div>
      </div>

      {/* FILTER RAIL — pill row, distinct from /leads' chip style. */}
      <div className="mb-4 rounded-2xl border hairline bg-white px-3 py-2.5 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
          {OUTBOUND_STATUSES.map((s) => {
            const Icon = s.icon;
            const active = filter === s.key;
            const count = counts[s.key] ?? 0;
            const label = scopedLabel(s.key, isAdmin);
            return (
              <button
                data-testid={`outbound-filter-${s.key}`}
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold whitespace-nowrap transition',
                  active ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'
                )}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2.4} /> {label}
                <span className={cn('mono text-[10.5px] tabular-nums', active ? 'text-white/60' : 'text-neutral-400')}>{count}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2 flex-1 sm:flex-none min-w-[220px]">
          <input
            data-testid="outbound-search"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, phone, campaign…"
            className="flex-1 rounded-lg border hairline bg-neutral-50 px-3 py-1.5 text-[12.5px] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
          />
          {isAdmin && (
            <OutboundLoFilter
              value={loFilter}
              onChange={setLoFilter}
              members={teamMembers}
            />
          )}
        </div>
      </div>

      {/* Selection bar — appears when the LO has hand-picked rows. */}
      {selectedIds.size > 0 && (
        <div data-testid="outbound-selection-bar" className="mb-3 rounded-xl border border-neutral-900 bg-neutral-900 text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[12.5px]">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--kf-gold))] text-neutral-950 font-black px-2 py-0.5 tabular-nums">
              {selectedIds.size}
            </span>
            <span className="font-semibold">lead{selectedIds.size === 1 ? '' : 's'} selected for the dialer</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="outbound-selection-clear"
              onClick={clearSelection}
              className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[11.5px] font-semibold hover:bg-white/10"
            >
              Clear
            </button>
            {isAdmin && (
              <ReassignPicker
                selectedCount={selectedIds.size}
                selectedIds={Array.from(selectedIds)}
                teamMembers={teamMembers}
                onDone={() => { clearSelection(); OutboundStore.refresh(); }}
              />
            )}
            <button
              data-testid="outbound-selection-start"
              onClick={startCalling}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--kf-gold))] text-neutral-950 px-3.5 py-1.5 text-[12px] font-bold hover:brightness-110"
            >
              <Play className="h-3.5 w-3.5" strokeWidth={3} /> Dial these {selectedIds.size}
            </button>
          </div>
        </div>
      )}

      {/* TABLE — dialer-first columns. Compact rows, big phone number. */}
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <OutboundTableHeader
          allSelected={allVisibleSelected}
          someSelected={someVisibleSelected && !allVisibleSelected}
          onToggleAll={selectAllVisible}
          disabled={filtered.length === 0}
          sort={sort}
          onSort={cycleSort}
        />
        {sorted.length === 0 && (
          <div className="px-6 py-16 text-center">
            {!loaded ? (
              <div className="text-neutral-500 text-[13px]">Loading…</div>
            ) : leads.length === 0 ? (
              <div>
                <PhoneOutgoing className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
                <div className="text-[13.5px] font-semibold text-neutral-700">No outbound leads yet</div>
                <div className="text-[12.5px] text-neutral-500 mt-1">
                  {isAdmin ? 'Import a CSV to get started.' : 'Ask an admin to import your list.'}
                </div>
                {isAdmin && (
                  <button
                    onClick={() => navigate('/outbound/import')}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 text-white px-3.5 py-2 text-[12.5px] font-semibold"
                  >
                    <Upload className="h-3.5 w-3.5" /> Import CSV
                  </button>
                )}
              </div>
            ) : (
              <div className="text-neutral-500 text-[13px]">No leads match this filter.</div>
            )}
          </div>
        )}
        <div className="divide-y divide-neutral-100">
          {sorted.map((lead) => (
            <OutboundRow
              key={lead.id}
              lead={lead}
              isAdmin={isAdmin}
              selected={selectedIds.has(lead.id)}
              onToggleSelect={(e) => toggleSelect(lead.id, e)}
              onOpenFocus={() => {
                // Freeze the current focusQueue at click-time (single-lead
                // pickup honors selection state).
                const q = focusQueue.length ? focusQueue : [lead];
                const idx = q.findIndex((l) => l.id === lead.id);
                setFocusList(q);
                setFocusIndex(idx === -1 ? 0 : idx);
              }}
            />
          ))}
        </div>
      </div>

      {/* Modals & overlays */}
      <LogCallModal
        lead={logCallLead}
        onClose={() => setLogCallLead(null)}
      />
      {focusIndex !== null && (
        <FocusMode
          queue={focusList || focusQueue}
          startIndex={focusIndex}
          onClose={() => { setFocusIndex(null); setFocusList(null); }}
        />
      )}
    </div>
  );
}

/* ── KPI tile ────────────────────────────────────────────────────── */
function KpiTile({ label, value, icon: Icon, tone = 'default' }) {
  const toneCls = {
    default: 'text-white',
    warn:    'text-amber-300',
    gold:    'text-[hsl(var(--kf-gold))]',
    sky:     'text-sky-300',
    violet:  'text-violet-300',
    hot:     'text-red-300',
  }[tone];
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur px-3.5 py-3 hover:bg-white/[0.06] transition">
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.16em] text-white/50">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={cn('mt-1.5 text-2xl font-black tabular-nums leading-none', toneCls)}>
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

/* ── Table header ────────────────────────────────────────────────── */
// Clickable header cell — cycles asc → desc → cleared. Neutral state
// shows a subtle double chevron so the LO knows it's sortable; the
// active column shows a filled arrow in the current direction.
function SortHeader({ label, colKey, sort, onSort, align = 'left', className = '' }) {
  const active = sort?.col === colKey;
  const alignCls = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <button
      type="button"
      onClick={() => onSort(colKey)}
      data-testid={`outbound-sort-${colKey}`}
      className={cn(
        'group inline-flex items-center gap-1 select-none cursor-pointer transition-colors',
        active ? 'text-neutral-900' : 'text-neutral-500 hover:text-neutral-700',
        alignCls,
        className,
      )}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span>{label}</span>
      {active
        ? (sort.dir === 'asc'
            ? <ChevronUp className="h-3 w-3" />
            : <ChevronDown className="h-3 w-3" />)
        : <ChevronsUpDown className="h-3 w-3 opacity-0 group-hover:opacity-70 transition-opacity" />}
    </button>
  );
}

/* ── Reassign dropdown (admin bulk action) ─────────────────────────
   Admin-only control living in the selection bar. Opens a popover with
   the active LO roster. Picking a target POSTs to the bulk-reassign
   endpoint; the picker also includes an "Unassign (back to pool)"
   escape hatch so an admin can un-claim leads without needing to pick
   a replacement first. */
function ReassignPicker({ selectedCount, selectedIds, teamMembers, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Only active LOs + admins who can be dialer owners should surface
  // as reassign targets. Filter mirrors the Outbound KPI tile's "my
  // queue" view so admins can't accidentally assign to a deactivated
  // teammate.
  const eligibleLOs = useMemo(() => {
    const list = Array.isArray(teamMembers) ? teamMembers : [];
    return list
      .filter((u) => u.is_active !== false && (u.role === 'lo' || u.dialer_eligible === true))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [teamMembers]);

  const reassign = async (targetId, targetName) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/outbound/bulk-reassign`, {
        lead_ids: selectedIds,
        assigned_to: targetId,
      });
      const n = r.data?.modified ?? 0;
      toast.success(`Reassigned ${n} lead${n === 1 ? '' : 's'} → ${targetName}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      // 404 == backend deployment hasn't caught up with the frontend
      // bundle (new /outbound/bulk-reassign route is missing on prod).
      // Give the admin a clearer signal than the raw "Not Found".
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        toast.error('Reassign endpoint not deployed yet — please try again in a minute');
      } else {
        toast.error(detail || 'Failed to reassign');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="outbound-reassign-trigger"
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy || selectedCount === 0}
        className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[11.5px] font-semibold hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Users className="h-3.5 w-3.5" />
        Reassign to…
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div
          data-testid="outbound-reassign-menu"
          className="absolute right-0 top-full mt-1.5 w-64 rounded-xl border border-neutral-200 bg-white text-neutral-900 shadow-xl z-30 overflow-hidden"
        >
          <div className="px-3.5 py-2.5 border-b border-neutral-100">
            <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Move to Loan Officer</div>
            <div className="text-[11px] text-neutral-600 mt-0.5">
              {selectedCount} lead{selectedCount === 1 ? '' : 's'} will leave the current queue
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {eligibleLOs.length === 0 && (
              <div className="px-3.5 py-3 text-[12px] text-neutral-500">No eligible loan officers.</div>
            )}
            {eligibleLOs.map((u) => (
              <button
                key={u.id}
                type="button"
                data-testid={`outbound-reassign-${u.id}`}
                onClick={() => reassign(u.id, u.name || u.email)}
                disabled={busy}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[12.5px] hover:bg-neutral-50 disabled:opacity-50"
              >
                <div className="h-6 w-6 shrink-0 rounded-full bg-neutral-900 text-white font-bold text-[9px] flex items-center justify-center">
                  {(u.initials || (u.name || '?').slice(0, 2)).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-semibold truncate">{u.name || u.email}</div>
                  <div className="text-[10.5px] text-neutral-500 truncate">{u.title || u.email}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="border-t border-neutral-100 py-1">
            <button
              type="button"
              data-testid="outbound-reassign-unassign"
              onClick={() => reassign(null, 'Unassigned pool')}
              disabled={busy}
              className="w-full px-3.5 py-2 text-left text-[12px] text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
            >
              <span className="font-semibold">Unassign</span>
              <span className="text-neutral-500"> — send back to the pool</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function OutboundTableHeader({ allSelected, someSelected, onToggleAll, disabled, sort, onSort }) {
  return (
    <div className="hidden md:grid grid-cols-12 gap-x-3 px-5 py-2.5 text-[9.5px] uppercase tracking-wider font-bold text-neutral-500 border-b hairline bg-neutral-50/50 items-center">
      <div className="col-span-1 flex items-center">
        <SelectCheckbox
          data-testid="outbound-select-all"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={onToggleAll}
          disabled={disabled}
          aria-label="Select all visible"
        />
      </div>
      <div className="col-span-2"><SortHeader label="Name"        colKey="name"        sort={sort} onSort={onSort} /></div>
      <div className="col-span-2"><SortHeader label="Phone"       colKey="phone"       sort={sort} onSort={onSort} /></div>
      <div className="col-span-1"><SortHeader label="State"       colKey="state"       sort={sort} onSort={onSort} align="center" className="w-full" /></div>
      <div className="col-span-1"><SortHeader label="Campaign"    colKey="campaign"    sort={sort} onSort={onSort} /></div>
      <div className="col-span-1"><SortHeader label="Attempts"    colKey="attempts"    sort={sort} onSort={onSort} align="center" className="w-full" /></div>
      <div className="col-span-1"><SortHeader label="Last Call"   colKey="last_call"   sort={sort} onSort={onSort} /></div>
      <div className="col-span-1"><SortHeader label="Appointment" colKey="appointment" sort={sort} onSort={onSort} /></div>
      <div className="col-span-1"><SortHeader label="Status"      colKey="status"      sort={sort} onSort={onSort} align="center" className="w-full" /></div>
      <div className="col-span-1"><SortHeader label="Created"     colKey="created"     sort={sort} onSort={onSort} /></div>
    </div>
  );
}

/* Small controlled checkbox with a proper indeterminate visual state.
   The toggle callback is wired to `onClick` (not `onChange`) because
   React's ChangeEvent on checkboxes drops `shiftKey` in some browsers.
   `onClick` reliably carries modifier keys so Shift-click range select
   can read `e.shiftKey` from the parent handler. A no-op `onChange`
   silences React's "controlled component without onChange" warning
   while the actual state update runs through the click handler. */
function SelectCheckbox({ checked, indeterminate, onChange, disabled, ariaLabel, ...rest }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={!!checked}
      onChange={() => {}}
      onClick={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'h-4 w-4 rounded border-2 border-neutral-300 text-neutral-900 focus:ring-2 focus:ring-neutral-900/20 accent-neutral-900 cursor-pointer',
        disabled && 'opacity-30 cursor-not-allowed'
      )}
      {...rest}
    />
  );
}

/* ── Table row ───────────────────────────────────────────────────── */
function OutboundRow({ lead, isAdmin, selected, onToggleSelect, onOpenFocus }) {
  const status = lead.outboundStatus || 'new';
  const styles = STATUS_STYLE[status] || STATUS_STYLE.new;
  const attempts = lead.outboundAttempts || 0;
  const assignee = isAdmin && lead.assignedTo ? TeamStore.get(lead.assignedTo) : null;

  return (
    <div
      data-testid={`outbound-row-${lead.id}`}
      className={cn(
        'grid grid-cols-1 md:grid-cols-12 gap-x-3 px-5 py-2.5 items-center hover:bg-neutral-50 group cursor-pointer transition-colors',
        selected && 'bg-[hsl(var(--kf-gold-soft))]/40 hover:bg-[hsl(var(--kf-gold-soft))]/60'
      )}
      onClick={onOpenFocus}
    >
      {/* Row-level selection checkbox. Stops propagation so a click on
          the box doesn't launch Focus Mode for that lead. */}
      <div className="col-span-1 flex items-center" onClick={(e) => e.stopPropagation()}>
        <SelectCheckbox
          data-testid={`outbound-select-${lead.id}`}
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Select ${lead.name}`}
        />
      </div>

      {/* Name + optional assignee dot (admins) */}
      <div className="col-span-2 flex items-center gap-2.5 min-w-0">
        <div className="relative h-7 w-7 shrink-0 rounded-full bg-neutral-900 text-white font-bold text-[10px] flex items-center justify-center">
          {lead.initials || '?'}
          {attempts > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-[hsl(var(--kf-gold))] text-neutral-900 text-[8.5px] font-black flex items-center justify-center border border-white"
              title={`${attempts} call attempt${attempts === 1 ? '' : 's'}`}
            >
              {attempts}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-neutral-900 truncate leading-tight">{lead.name}</div>
          {isAdmin && assignee && (
            <div className="text-[10.5px] text-neutral-500 truncate">→ {assignee.name?.split(' ')[0] || assignee.initials}</div>
          )}
        </div>
      </div>

      {/* Phone — the star of the row. Bigger, tabular. Click-to-dial via
          `tel:` link. `stopPropagation` keeps the row-open handler from
          firing when the LO taps the number itself; digits stripped so
          formatted values still route to the OS dialer cleanly.

          IMPORTANT: `inline-block w-fit` (not `block`) so the clickable
          hit-area hugs the digits only. An LO hovering in the blank
          space of the Phone column must NOT fire the dialer by
          accident — a misclick there used to call the borrower. */}
      <div className="col-span-2 min-w-0">
        {lead.phone ? (
          <a
            href={`tel:${(lead.phone || '').replace(/\D/g, '')}`}
            onClick={(e) => e.stopPropagation()}
            className="text-[12.5px] text-neutral-700 tabular-nums inline-block w-fit max-w-full hover:text-neutral-900 hover:underline underline-offset-2"
            data-testid={`outbound-row-phone-${lead.id}`}
          >
            {formatPhone(lead.phone)}
          </a>
        ) : (
          <div className="text-[12.5px] text-neutral-400 tabular-nums">—</div>
        )}
      </div>

      {/* State */}
      <div className="col-span-1 text-center text-[12.5px] font-semibold text-neutral-700 tabular-nums">
        {deriveState(lead)}
      </div>

      {/* Campaign */}
      <div className="col-span-1 min-w-0">
        <div className="text-[12px] text-neutral-700 truncate" title={lead.outboundCampaign || ''}>
          {lead.outboundCampaign || '—'}
        </div>
      </div>

      {/* Attempts */}
      <div className="col-span-1 text-center">
        <span className={cn(
          'inline-flex items-center justify-center min-w-[24px] px-1.5 rounded-md text-[11.5px] font-bold tabular-nums',
          attempts === 0 ? 'bg-neutral-100 text-neutral-500' :
          attempts < 3 ? 'bg-amber-100 text-amber-800' :
          'bg-red-100 text-red-700'
        )}>{attempts}</span>
      </div>

      {/* Last call result */}
      <div className="col-span-1 min-w-0">
        <div className="text-[11.5px] text-neutral-500 leading-tight">{formatRelative(lead.outboundLastCallAt)}</div>
        {lead.outboundLastCallResult && (
          <div className="text-[10.5px] font-semibold text-neutral-700 truncate">{CALL_RESULT_LABEL[lead.outboundLastCallResult] || lead.outboundLastCallResult}</div>
        )}
      </div>

      {/* Appointment (formerly Next Follow-Up) */}
      <div className="col-span-1 min-w-0">
        <div className="text-[11.5px] font-semibold text-neutral-700 truncate">{formatFuture(lead.outboundNextFollowUpAt)}</div>
      </div>

      {/* Status pill */}
      <div className="col-span-1 flex justify-center">
        <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold whitespace-nowrap', styles.chip)}>
          <span className={cn('h-1.5 w-1.5 rounded-full', styles.dot)} />
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      {/* Created (import date) — pulled from lead.created_at, stamped
          by the outbound importer at row-insert time. Placed last so
          the row's most-actionable columns stay left. */}
      <div className="col-span-1 min-w-0 text-left">
        <div className="text-[11.5px] font-semibold text-neutral-700 truncate tabular-nums" title={formatFullDate(lead.created_at)}>
          {formatCreated(lead.created_at)}
        </div>
      </div>
    </div>
  );
}


/* ── Admin LO filter (compact pill: "LO · All LOs ▾") ───────────────
   Deliberately tighter than the Leads AssigneeFilter — sits inline
   with the search box on the Outbound filter rail. */
function OutboundLoFilter({ value, onChange, members }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // LOs first, then admins/other roles. Filter to active only.
  const sorted = useMemo(() => {
    const list = (members || []).filter((m) => m.is_active !== false);
    return list.sort((a, b) => {
      if (a.role !== b.role) return a.role === 'lo' ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [members]);

  const label =
    value === 'all'        ? 'All LOs' :
    value === 'unassigned' ? 'Unassigned' :
    (sorted.find((m) => m.id === value)?.name || 'All LOs');

  const isFiltered = value !== 'all';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="outbound-lo-filter-trigger"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'h-8 pl-1.5 pr-2.5 rounded-full border hairline bg-white inline-flex items-center gap-1.5 text-[12px] font-semibold transition',
          isFiltered ? 'border-neutral-900 text-neutral-900' : 'text-neutral-700 hover:border-neutral-400',
        )}
      >
        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-neutral-100 text-[9.5px] font-black text-neutral-500 tracking-tight">
          LO
        </span>
        <span className="truncate max-w-[120px]">{label}</span>
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="outbound-lo-filter-menu"
          className="absolute right-0 top-full mt-1.5 z-30 w-[240px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="py-1 max-h-[320px] overflow-y-auto">
            <LoOption
              active={value === 'all'}
              onClick={() => { onChange('all'); setOpen(false); }}
              label="All LOs"
              sub={`${sorted.length} team member${sorted.length === 1 ? '' : 's'}`}
              testId="outbound-lo-filter-all"
            />
            <LoOption
              active={value === 'unassigned'}
              onClick={() => { onChange('unassigned'); setOpen(false); }}
              label="Unassigned"
              sub="No LO yet"
              testId="outbound-lo-filter-unassigned"
            />
            <div className="my-1 mx-3 border-t hairline" />
            {sorted.length === 0 ? (
              <div className="px-3 py-2 text-[11.5px] italic text-neutral-400">No team members yet.</div>
            ) : sorted.map((m) => (
              <LoOption
                key={m.id}
                active={value === m.id}
                onClick={() => { onChange(m.id); setOpen(false); }}
                label={m.name}
                sub={m.title || (m.role === 'admin' ? 'Administrator' : 'Broker')}
                testId={`outbound-lo-filter-${m.id}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LoOption({ active, onClick, label, sub, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'w-full text-left px-3 py-2 transition flex items-center justify-between gap-3',
        active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
      )}
    >
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold truncate">{label}</div>
        <div className="text-[10.5px] text-neutral-500 truncate">{sub}</div>
      </div>
      {active && <span className="h-1.5 w-1.5 rounded-full bg-neutral-900 shrink-0" />}
    </button>
  );
}
